import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { FaArrowLeft, FaPhoneAlt, FaEnvelope, FaMapMarkerAlt, FaGlobe, FaWhatsapp } from 'react-icons/fa';
import { formatLocationName } from '../utils/distributorData';
import { getDistributorsByLocationName } from '../services/distributorService';

const DistribuidorDetalle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [locationName, setLocationName] = useState('');
  const [distribuidores, setDistribuidores] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    const loadDistributors = async () => {
      setIsLoading(true);
      
      if (!id) {
        console.error("No ID parameter found in URL");
        setLocationName("Ubicación no encontrada");
        setDistribuidores([]);
        setIsLoading(false);
        return;
      }
      
      try {
        // Convert slug to proper location name
        const formattedName = formatLocationName(id);
        setLocationName(formattedName);
        
        // Fetch distributors for this location
        const distributorsData = await getDistributorsByLocationName(formattedName);
        setDistribuidores(distributorsData);
      } catch (error) {
        console.error("Error loading distributor data:", error);
        setDistribuidores([]);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadDistributors();
  }, [id, navigate]);
  
  // More comprehensive list of Mexican states
  const mexicanStateIds = [
    'aguascalientes', 'baja-california', 'baja-california-sur', 'campeche', 
    'chiapas', 'chihuahua', 'ciudad-de-mexico', 'coahuila', 'colima', 
    'durango', 'estado-de-mexico', 'guanajuato', 'guerrero', 'hidalgo', 
    'jalisco', 'michoacan', 'morelos', 'nayarit', 'nuevo-leon', 'oaxaca', 
    'puebla', 'queretaro', 'quintana-roo', 'san-luis-potosi', 'sinaloa', 
    'sonora', 'tabasco', 'tamaulipas', 'tlaxcala', 'veracruz', 'yucatan', 'zacatecas'
  ];
  
  // Check if this is a Mexican state
  const isMexicanState = id ? mexicanStateIds.includes(id.toLowerCase()) : false;
  
  if (isLoading) {
    return (
      <div className="pt-32 pb-16 container mx-auto px-4 text-center">
        <div className="inline-block w-8 h-8 border-4 border-[#0055B8] border-t-transparent rounded-full animate-spin"></div>
        <p className="mt-4 text-gray-600">Cargando información de distribuidores...</p>
      </div>
    );
  }
  
  return (
    <>
      <SEO 
        title={`Distribuidores en ${locationName} | ECOSOFT`}
        description={`Encuentra distribuidores autorizados de OPUS en ${locationName}. Adquiere nuestros productos a través de nuestra red oficial.`}
        keywords={`OPUS, distribuidores ${locationName}, comprar OPUS, ECOSOFT`}
      />
      
      {/* Hero Section */}
      <section id="hero" className="relative h-[450px] bg-gradient-to-r from-[#003171] to-[#0066CC]">
        <div className="absolute inset-0 bg-black/30"></div>
        <div className="container mx-auto px-4 h-full flex items-center pt-14 relative z-10">
          <div className="max-w-2xl text-white">
            <Link to="/distribuidores" className="inline-flex items-center mb-6 text-gray-200 hover:text-white">
              <FaArrowLeft className="mr-2" /> Volver a distribuidores
            </Link>
            <h1 className="text-5xl font-bold mb-4">Distribuidores en {locationName}</h1>
            <p className="text-xl">
              Encuentra tu distribuidor autorizado más cercano
            </p>
          </div>
        </div>
      </section>
      
      {/* Distribuidores List */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto space-y-8">
            {distribuidores.length > 0 ? (
              distribuidores.map((dist, index) => (
                <div key={index} className="bg-[#F5F7FA] p-6 rounded-lg shadow-sm">
                  <h2 className="text-2xl font-bold mb-4 text-[#1A2B39]">{dist.Name}</h2>
                  
                  <div className="space-y-3">
                    {dist.Address && (
                      <div className="flex items-start">
                        <FaMapMarkerAlt className="text-[#0055B8] mt-1 mr-3 flex-shrink-0" />
                        <p>{dist.Address}</p>
                      </div>
                    )}
                    
                    {dist.Phone && (
                      <div className="flex items-start">
                        <FaPhoneAlt className="text-[#0055B8] mt-1 mr-3 flex-shrink-0" />
                        <p>{dist.Phone}</p>
                      </div>
                    )}
                    
                    {dist.Email && (
                      <div className="flex items-start">
                        <FaEnvelope className="text-[#0055B8] mt-1 mr-3 flex-shrink-0" />
                        <a href={`mailto:${dist.Email}`} className="text-[#0055B8] hover:underline">{dist.Email}</a>
                      </div>
                    )}
                    
                    {dist.Website && (
                      <div className="flex items-start">
                        <FaGlobe className="text-[#0055B8] mt-1 mr-3 flex-shrink-0" />
                        <a 
                          href={dist.Website.startsWith('http') ? dist.Website : `https://${dist.Website}`} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="text-[#0055B8] hover:underline"
                        >
                          {dist.Website}
                        </a>
                      </div>
                    )}
                    
                    {/* Additional social links */}
                    {dist['Button 1 Link'] && (
                      <div className="flex items-start">
                        <FaGlobe className="text-[#0055B8] mt-1 mr-3 flex-shrink-0" />
                        <a 
                          href={dist['Button 1 Link'].startsWith('http') ? dist['Button 1 Link'] : `https://${dist['Button 1 Link']}`} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="text-[#0055B8] hover:underline"
                        >
                          {dist['Button 1 Text'] || 'Enlace adicional'}
                        </a>
                      </div>
                    )}
                    
                    {/* Description if available */}
                    {dist.Description && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <p className="text-gray-600">{dist.Description}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="bg-[#F5F7FA] p-6 rounded-lg shadow-sm text-center">
                <p className="text-lg text-gray-600">No se encontraron distribuidores para esta ubicación.</p>
              </div>
            )}
          </div>
          
          <div className="max-w-4xl mx-auto mt-12 p-6 bg-[#1A2B39]/5 rounded-lg">
            <h3 className="text-xl font-bold mb-4 text-[#1A2B39]">¿No encuentras un distribuidor cerca?</h3>
            <p className="mb-6">Siempre puedes contactar directamente con ECOSOFT para adquirir nuestros productos:</p>
            <div className="flex flex-wrap gap-3">
              <Link 
                to="/contacto" 
                className="px-6 py-3 bg-[#0055B8] hover:bg-[#0066CC] text-white font-medium rounded-md inline-block"
              >
                Contactar a ECOSOFT
              </Link>
              <a 
                href="https://wa.me/525539908312" 
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 py-3 bg-[#25D366] hover:bg-[#20BD5C] text-white font-medium rounded-md inline-flex items-center"
              >
                <FaWhatsapp className="mr-2" /> WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default DistribuidorDetalle; 